import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useLang } from '../context/LangContext';

const content = {
  en: {
    pageTitle: 'Online Booking',
    metaTitle: 'Book Your Consultation | Halo Aesthetic Clinic Richmond Hill',
    pageDesc: 'Fill in the information below and we will contact you soon',
    metaDesc: 'Schedule your medical aesthetic consultation or treatment online with licensed professionals at Halo Aesthetic Clinic in Richmond Hill, ON.',
    name: 'Name',
    phone: 'Phone',
    service: 'Service',
    selectService: 'Please select a service',
    date: 'Appointment Date',
    time: 'Appointment Time',
    selectTime: 'Please select a time slot',
    notes: 'Notes',
    notesPlaceholder: 'Please enter your special requirements or other notes',
    submit: 'Submit Booking',
    successTitle: 'Booking Successful!',
    successMessage: 'Your booking request has been sent. We will contact you soon to confirm the details.',
    errorMessage: 'Failed to send booking request. Please try again.',
    backToBooking: 'Back to Booking',
    services: [
      { id: 'injection', name: 'Consult about Injection Beauty' },
      { id: 'skin', name: 'Consult about Skin Care' },
      { id: 'body', name: 'Consult about Body Shaping' },
      { id: 'ultherapy', name: 'Ulthera Prime' },
      { id: 'picosure_pro', name: 'PicoSure Pro' },
      { id: 'sculptra', name: 'Sculptra' },
    ],
  },
  cn: {
    pageTitle: '在线预约',
    metaTitle: '在线预约咨询与治疗 | Halo Richmond Hill 医美诊所',
    pageDesc: '填写以下信息，我们将尽快与您联系',
    metaDesc: '在线快捷预约 Halo Richmond Hill 医美诊所的持牌医疗专家团队咨询与医美疗程服务。',
    name: '姓名',
    phone: '电话',
    service: '服务项目',
    selectService: '请选择服务项目',
    date: '预约日期',
    time: '预约时间',
    selectTime: '请选择时间段',
    notes: '备注信息',
    notesPlaceholder: '请输入您的特殊需求或其他备注信息',
    submit: '提交预约',
    successTitle: '预约信息已准备发送！',
    successMessage: '预约信息已准备好，请在打开的邮件中检查并发送。',
    backToBooking: '返回预约',
    services: [
      { id: 'injection', name: '咨询注射美容' },
      { id: 'skin', name: '咨询皮肤护理' },
      { id: 'body', name: '咨询身体塑形' },
      { id: 'ultherapy', name: '超声刀Prime' },
      { id: 'picosure_pro', name: '蜂巢皮秒' },
      { id: 'sculptra', name: '童颜针' },
    ],
  }
};

const Booking = () => {
  const navigate = useNavigate();
  const { lang } = useLang();
  const c = content[lang];

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    service: '',
    date: '',
    time: '',
    notes: '',
  });

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // 时间段选项
  const timeSlots = [
    '11:00',
    '12:00',
    '13:00',
    '14:00',
    '15:00',
    '16:00',
    '17:00',
    '18:00',
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      const selectedService = c.services.find(s => s.id === formData.service)?.name || formData.service || '';

      const response = await fetch('https://halo-aesthetic-clinic.onrender.com/api/booking', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          service: selectedService
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to send booking request');
      }

      // Track booking conversion success in Google Tag Manager dataLayer
      if (window.dataLayer) {
        window.dataLayer.push({
          event: 'booking_conversion_success',
          formType: 'consultation_booking',
          timestamp: new Date().toISOString()
        });
      }

      setIsSubmitted(true);
      navigate('/booking-success', { state: { serviceName: selectedService } });
    } catch (err) {
      setError(c.errorMessage);
      console.error('Booking error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="py-16">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-white rounded-lg shadow-md p-8">
            <svg
              className="w-16 h-16 text-green-600 mx-auto mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
            <h2 className="text-2xl font-bold mb-4">{c.successTitle}</h2>
            <p className="text-gray-600 mb-6">
              {c.successMessage}
            </p>
            <button
              onClick={() => setIsSubmitted(false)}
              className="bg-green-600 text-white px-6 py-2 rounded-md hover:bg-green-700 transition-colors"
            >
              {c.backToBooking}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const canonicalUrl = 'https://www.haloaestheticclinic.com/booking';

  return (
    <div className="bg-softGradient bg-no-repeat bg-top text-text">
      <Helmet>
        <title>{c.metaTitle}</title>
        <meta name="description" content={c.metaDesc} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={c.metaTitle} />
        <meta property="og:description" content={c.metaDesc} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        {/* Event snippet for 网页浏览 conversion page
In your html page, add the snippet and call gtag_report_conversion when someone clicks on the chosen link or button. */}
        <script>
          {`function gtag_report_conversion(url) {
  var callback = function () {
    if (typeof(url) != 'undefined') {
      window.location = url;
    }
  };
  gtag('event', 'conversion', {
      'send_to': 'AW-18458381832/6SnuCPThvpcdEIic0uFE',
      'value': 1.0,
      'currency': 'CAD',
      'event_callback': callback
  });
  return false;
}`}
        </script>
      </Helmet>
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-section">
        {/* 页面标题 */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4 text-gray-900">{c.pageTitle}</h1>
          <p className="text-xl text-gray-700">
            {c.pageDesc}
          </p>
        </div>

        {/* 预约表单 */}
        <form onSubmit={handleSubmit} className="bg-white/80 backdrop-blur-sm rounded-xl shadow-card p-8 border border-champagne/40">
          <div className="space-y-6">
            {/* 姓名 */}
            <div>
              <label
                htmlFor="name"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                {c.name}
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
              />
            </div>

            {/* 电话 */}
            <div>
              <label
                htmlFor="phone"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                {c.phone}
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
              />
            </div>

            {/* 服务项目 */}
            <div>
              <label
                htmlFor="service"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                {c.service}
              </label>
              <select
                id="service"
                name="service"
                value={formData.service}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
              >
                <option value="">{c.selectService}</option>
                {c.services.map((service) => (
                  <option key={service.id} value={service.id}>
                    {service.name}
                  </option>
                ))}
              </select>
            </div>

            {/* 日期 */}
            <div>
              <label
                htmlFor="date"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                {c.date}
              </label>
              <input
                type="date"
                id="date"
                name="date"
                value={formData.date}
                onChange={handleChange}
                required
                min={new Date().toISOString().split('T')[0]}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
              />
            </div>

            {/* 时间段 */}
            <div>
              <label
                htmlFor="time"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                {c.time}
              </label>
              <select
                id="time"
                name="time"
                value={formData.time}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
              >
                <option value="">{c.selectTime}</option>
                {timeSlots.map((time) => (
                  <option key={time} value={time}>
                    {time}
                  </option>
                ))}
              </select>
            </div>

            {/* 备注 */}
            <div>
              <label
                htmlFor="notes"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                {c.notes}
              </label>
              <textarea
                id="notes"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows={4}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                placeholder={c.notesPlaceholder}
              />
            </div>

            {/* Error message */}
            {error && (
              <div className="text-red-600 text-center mb-4">
                {error}
              </div>
            )}

            {/* 提交按钮 */}
            <button
              type="submit"
              disabled={isLoading}
              className={`w-full py-3 rounded-xl shadow-soft transition-all text-gray-900 hover:shadow-glow hover:brightness-110 border border-champagne/60 ${isLoading ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              style={{
                backgroundImage:
                  'linear-gradient(90deg, rgba(217,195,169,1) 0%, rgba(217,195,169,0.8) 40%, rgba(217,195,169,0.3) 100%)'
              }}
            >
              {isLoading ? 'Sending...' : c.submit}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Booking; 